import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
    DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { PasswordInput } from '@/components/PasswordInput';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { User2, Settings, LogOut, KeyRound, Camera, ChevronDown, Loader2 } from 'lucide-react';

const ROLE_TINT = {
    admin: 'bg-slate-100 text-slate-800 border-slate-200',
    sales: 'bg-blue-100 text-blue-800 border-blue-200',
    care: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    installation: 'bg-amber-100 text-amber-800 border-amber-200',
    vendor: 'bg-purple-100 text-purple-800 border-purple-200',
    customer: 'bg-orange-100 text-orange-800 border-orange-200',
};
const ROLE_LABEL = {
    admin: 'Administrator', sales: 'Sales', care: 'Customer Care',
    installation: 'Installation', vendor: 'Vendor', customer: 'Customer',
};

/**
 * Unified header user menu: avatar → dropdown with Profile / Change Password / Logout.
 * Works for every logged-in role.
 */
export function UserMenu({ align = 'end' }) {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const [dialogOpen, setDialogOpen] = useState(false);
    if (!user) return null;
    const initials = (user.name || user.email || '?')
        .split(/\s|@/)
        .filter(Boolean)
        .slice(0, 2)
        .map(w => w[0])
        .join('')
        .toUpperCase();
    const doLogout = () => {
        logout();
        nav('/');
        toast.success('Logged out');
    };
    return (
        <>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <button className='flex items-center gap-2 rounded-full pl-1 pr-2 py-1 border bg-card hover:bg-muted transition text-sm' data-testid='user-menu-trigger'>
                        <Avatar className='w-7 h-7'>
                            {user.avatar_base64 && <AvatarImage src={user.avatar_base64} alt={user.name} />}
                            <AvatarFallback className='text-xs bg-gradient-to-br from-primary/80 to-primary text-primary-foreground font-semibold'>{initials}</AvatarFallback>
                        </Avatar>
                        <span className='hidden md:inline max-w-[130px] truncate font-medium'>{user.name || user.email}</span>
                        <ChevronDown className='w-3.5 h-3.5 text-muted-foreground' />
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align={align} className='w-64' data-testid='user-menu-content'>
                    <div className='px-3 py-2'>
                        <div className='flex items-center gap-2.5'>
                            <Avatar className='w-10 h-10'>
                                {user.avatar_base64 && <AvatarImage src={user.avatar_base64} alt={user.name} />}
                                <AvatarFallback className='bg-gradient-to-br from-primary/80 to-primary text-primary-foreground font-semibold'>{initials}</AvatarFallback>
                            </Avatar>
                            <div className='min-w-0 flex-1'>
                                <div className='font-semibold text-sm truncate'>{user.name || 'User'}</div>
                                <div className='text-[11px] text-muted-foreground truncate'>{user.email || user.mobile}</div>
                            </div>
                        </div>
                        <Badge variant='outline' className={`mt-2 text-[10px] ${ROLE_TINT[user.role] || ''}`}>
                            {ROLE_LABEL[user.role] || user.role}
                        </Badge>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onSelect={() => setDialogOpen(true)} data-testid='user-menu-profile'>
                        <Settings className='w-4 h-4 mr-2' /> Update Profile
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onSelect={doLogout} className='text-destructive focus:text-destructive' data-testid='user-menu-logout'>
                        <LogOut className='w-4 h-4 mr-2' /> Logout
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>

            <ProfileDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
        </>
    );
}

function ProfileDialog({ open, onClose }) {
    const { user, updateProfile } = useAuth();
    const [tab, setTab] = useState('profile');
    const [form, setForm] = useState({ name: '', email: '', mobile: '', address: '', city: '', district: '' });
    const [avatar, setAvatar] = useState('');
    const [pw, setPw] = useState({ current_password: '', new_password: '', confirm: '' });
    const [saving, setSaving] = useState(false);

    // Reset form when opened
    const initFromUser = () => {
        setForm({
            name: user?.name || '', email: user?.email || '', mobile: user?.mobile || '',
            address: user?.address || '', city: user?.city || '', district: user?.district || '',
        });
        setAvatar(user?.avatar_base64 || '');
        setPw({ current_password: '', new_password: '', confirm: '' });
        setTab('profile');
    };
    // Init form on open
    if (open && !form.name && user) {
        // fire once
        setTimeout(initFromUser, 0);
    }

    const onAvatarPick = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (!f.type.startsWith('image/')) return toast.error('Please pick an image');
        if (f.size > 2 * 1024 * 1024) return toast.error('Image must be under 2 MB');
        const r = new FileReader();
        r.onload = () => setAvatar(r.result);
        r.readAsDataURL(f);
    };

    const saveProfile = async () => {
        if (!form.name?.trim()) return toast.error('Name is required');
        setSaving(true);
        try {
            const payload = { ...form };
            if (avatar && avatar !== user?.avatar_base64) payload.avatar_base64 = avatar;
            await updateProfile(payload);
            toast.success('Profile updated');
            onClose();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to update');
        } finally { setSaving(false); }
    };

    const savePassword = async () => {
        if (!pw.current_password || !pw.new_password) return toast.error('Fill both fields');
        if (pw.new_password.length < 6) return toast.error('Password must be at least 6 characters');
        if (pw.new_password !== pw.confirm) return toast.error('New passwords do not match');
        setSaving(true);
        try {
            await api.changePassword(pw.current_password, pw.new_password);
            toast.success('Password changed');
            setPw({ current_password: '', new_password: '', confirm: '' });
            onClose();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to change password');
        } finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={o => !o && onClose()}>
            <DialogContent className='max-w-lg' data-testid='profile-dialog'>
                <DialogHeader>
                    <DialogTitle className='flex items-center gap-2'>
                        <User2 className='w-5 h-5 text-primary' /> My Account
                    </DialogTitle>
                </DialogHeader>
                <Tabs value={tab} onValueChange={setTab}>
                    <TabsList className='w-full'>
                        <TabsTrigger value='profile' className='flex-1' data-testid='profile-tab-info'>
                            <Settings className='w-3.5 h-3.5 mr-1.5' /> Profile
                        </TabsTrigger>
                        <TabsTrigger value='password' className='flex-1' data-testid='profile-tab-password'>
                            <KeyRound className='w-3.5 h-3.5 mr-1.5' /> Password
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value='profile' className='space-y-3 mt-3'>
                        {/* Avatar */}
                        <div className='flex items-center gap-3'>
                            <Avatar className='w-16 h-16'>
                                {avatar && <AvatarImage src={avatar} alt='avatar' />}
                                <AvatarFallback className='text-lg bg-gradient-to-br from-primary/80 to-primary text-primary-foreground font-bold'>
                                    {(form.name || user?.email || '?').slice(0, 1).toUpperCase()}
                                </AvatarFallback>
                            </Avatar>
                            <label className='inline-flex items-center gap-1 text-xs cursor-pointer h-8 px-2.5 rounded-md border hover:bg-muted transition'>
                                <Camera className='w-3.5 h-3.5' /> Change photo
                                <input type='file' accept='image/*' className='hidden' onChange={onAvatarPick} data-testid='profile-avatar' />
                            </label>
                            {avatar && (
                                <Button type='button' size='sm' variant='ghost' onClick={() => setAvatar('')} className='text-xs'>
                                    Remove
                                </Button>
                            )}
                        </div>
                        <div className='grid grid-cols-2 gap-3'>
                            <div className='space-y-1 col-span-2'>
                                <Label>Full name *</Label>
                                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid='profile-name' />
                            </div>
                            <div className='space-y-1'>
                                <Label>Email</Label>
                                <Input type='email' value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid='profile-email' />
                            </div>
                            <div className='space-y-1'>
                                <Label>Mobile</Label>
                                <Input inputMode='numeric' value={form.mobile} onChange={e => setForm({ ...form, mobile: e.target.value.replace(/\D/g, '').slice(0, 10) })} data-testid='profile-mobile' />
                            </div>
                            <div className='space-y-1 col-span-2'>
                                <Label>Address</Label>
                                <Textarea rows={2} value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} data-testid='profile-address' />
                            </div>
                            <div className='space-y-1'>
                                <Label>City</Label>
                                <Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} data-testid='profile-city' />
                            </div>
                            <div className='space-y-1'>
                                <Label>District</Label>
                                <Input value={form.district} onChange={e => setForm({ ...form, district: e.target.value })} data-testid='profile-district' />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant='outline' onClick={onClose} data-testid='profile-cancel'>Cancel</Button>
                            <Button onClick={saveProfile} disabled={saving} data-testid='profile-save'>
                                {saving && <Loader2 className='w-3.5 h-3.5 mr-1 animate-spin' />} Save changes
                            </Button>
                        </DialogFooter>
                    </TabsContent>

                    <TabsContent value='password' className='space-y-3 mt-3'>
                        <div className='space-y-1'>
                            <Label>Current password</Label>
                            <PasswordInput value={pw.current_password} onChange={e => setPw({ ...pw, current_password: e.target.value })} data-testid='pw-current' />
                        </div>
                        <div className='space-y-1'>
                            <Label>New password</Label>
                            <PasswordInput value={pw.new_password} onChange={e => setPw({ ...pw, new_password: e.target.value })} data-testid='pw-new' />
                            <p className='text-[11px] text-muted-foreground'>At least 6 characters.</p>
                        </div>
                        <div className='space-y-1'>
                            <Label>Confirm new password</Label>
                            <PasswordInput value={pw.confirm} onChange={e => setPw({ ...pw, confirm: e.target.value })} data-testid='pw-confirm' />
                        </div>
                        <DialogFooter>
                            <Button variant='outline' onClick={onClose}>Cancel</Button>
                            <Button onClick={savePassword} disabled={saving} data-testid='pw-save'>
                                {saving && <Loader2 className='w-3.5 h-3.5 mr-1 animate-spin' />} Change password
                            </Button>
                        </DialogFooter>
                    </TabsContent>
                </Tabs>
            </DialogContent>
        </Dialog>
    );
}

export default UserMenu;
